import { STATUS_LABELS, type BoardSnapshot, type Card } from '@blueprint/domain'
import styles from './HistoricalBoardView.module.css'

const ordered = <T extends { id: string; position: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id))

function HistoricalCard({ card }: { card: Card }) {
  return (
    <details className={styles.card}>
      <summary>{card.title}</summary>
      <div className={styles.cardBody}>
        <p>{STATUS_LABELS[card.status]}{card.owner ? ` · ${card.owner}` : ''}</p>
        {card.description ? <p>{card.description}</p> : null}
        {card.tags.length ? <p>Tags: {card.tags.join(', ')}</p> : null}
        <details>
          <summary>Full card data</summary>
          <pre>{JSON.stringify(card, null, 2)}</pre>
        </details>
      </div>
    </details>
  )
}

export function HistoricalBoardView({ revision, snapshot }: { revision: number; snapshot: BoardSnapshot }) {
  const columns = ordered(snapshot.columns)
  const lanes = ordered(snapshot.swimlanes)
  const cardsByCell = new Map<string, Card[]>()
  for (const card of snapshot.cards) {
    const cell = `${card.rowId}|${card.columnId}`
    cardsByCell.set(cell, [...(cardsByCell.get(cell) ?? []), card])
  }

  return (
    <section className={styles.shell} aria-label={`Board revision ${revision}`}>
      <div className={styles.notice} role="status">
        Viewing saved revision {revision} of {snapshot.board.name}. This version is read only; select Current to edit.
      </div>
      <div className={styles.scroll}>
        <div className={styles.grid} style={{ '--cols': columns.length } as React.CSSProperties}>
          <div className={styles.corner}>Row</div>
          {columns.map((column) => <div className={styles.column} key={column.id}>{column.title}</div>)}
          {lanes.map((lane) => (
            <div className={styles.laneGroup} key={lane.id}>
              <div className={styles.lane}>{lane.title}</div>
              {ordered(snapshot.rows.filter((row) => row.swimlaneId === lane.id)).map((row) => (
                <div className={styles.row} key={row.id}>
                  <div className={styles.rowTitle}>{row.title}</div>
                  {columns.map((column) => (
                    <div className={styles.cell} key={column.id}>
                      {ordered(cardsByCell.get(`${row.id}|${column.id}`) ?? []).map((card) => (
                        <HistoricalCard card={card} key={card.id} />
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
